import {AvailabilityType, RichResultsTypes} from '../enum/rich-results-types.enum';

export interface Base {
  '@type': RichResultsTypes | string;
}

// Core types
export interface Thing extends Base {
  '@type': RichResultsTypes.Thing | string;
  name?: string;
  alternateName?: string;
  url?: string;
  image?: string | ImageObject;
  sameAs?: string | string[];
  identifier?: string;
  description?: string;
}

export interface Organization extends Base {
  '@type': RichResultsTypes.Organization;
  name: string;
  url: string;
  logo?: string;
  sameAs?: string[];
}

export interface Person extends Base {
  '@type': RichResultsTypes.Person;
  name: string;
}

export interface ImageObject extends Base {
  '@type': RichResultsTypes.ImageObject;
  url: string;
  width?: number;
  height?: number;
}

// List / navigation
export interface ListItem extends Base {
  '@type': RichResultsTypes.ListItem;
  position: number;
  item: string | Thing | { id: string, name: string; url?: string };
}

export interface ItemList extends Base {
  '@type': RichResultsTypes.ItemList;
  itemListElement: (ListItem | Product | Thing)[];
}

export interface BreadcrumbList extends Base {
  '@type': RichResultsTypes.BreadcrumbList;
  itemListElement: ListItem[];
}

// FAQ / Q&A
export interface FAQPage extends Base {
  '@type': RichResultsTypes.FAQPage;
  mainEntity: {
    '@type': RichResultsTypes.Question;
    name: string;
    acceptedAnswer: {
      '@type': RichResultsTypes.Answer;
      text: string;
    };
  }[];
}

export interface QAPage extends Base {
  '@type': RichResultsTypes.QAPage;
  mainEntity: {
    '@type': RichResultsTypes.Question;
    name: string;
    acceptedAnswer: {
      '@type': RichResultsTypes.Answer;
      text: string;
    };
  };
}

// Ratings / Reviews
export interface AggregateRating extends Base {
  '@type': RichResultsTypes.AggregateRating;
  ratingValue: number;
  reviewCount?: number;
}

export interface Review extends Base {
  '@type': RichResultsTypes.Review;
  reviewBody?: string;
  reviewRating?: AggregateRating;
  author?: Person | Organization;
}

// Product / Offers
export interface Offer extends Base {
  '@type': RichResultsTypes.Offer;
  price: string | number;
  priceCurrency: string;
  priceValidUntil: string;
  availability?: AvailabilityType;
  url?: string;
}

export interface AggregateOffer extends Base {
  '@type': RichResultsTypes.AggregateOffer;
  lowPrice?: string | number;
  highPrice?: string | number;
  priceCurrency?: string;
  offerCount?: number;
}

export interface Product extends Base {
  '@type': RichResultsTypes.Product;
  name: string;
  description?: string;
  image?: string | ImageObject;
  sku?: string;
  brand?: string | Organization;
  offers?: Offer | AggregateOffer;
  aggregateRating?: AggregateRating;
  review?: Review[];
  url?: string;
}

// Software / App
export interface SoftwareApplication extends Base {
  '@type': RichResultsTypes.SoftwareApplication;
  name: string;
  description?: string;
  applicationCategory?: string;
  operatingSystem?: string;
  offers?: Offer;
  url?: string;
}

// Dataset
export interface Dataset extends Base {
  '@type': RichResultsTypes.Dataset;
  description?: string;
  creator?: Person | Organization;
}

// Article
export interface Article extends Base {
  '@type': RichResultsTypes.Article;
  headline: string;
  articleBody: string;
  image?: string[];
  author?: Person | Organization;
  datePublished?: string;
  description?: string;
}

// Video
export interface VideoObject extends Base {
  '@type': RichResultsTypes.VideoObject;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  contentUrl?: string;
  embedUrl?: string;
}

// Web / Page Elements
export interface HowToSection extends Base {
  '@type': RichResultsTypes.HowToSection;
  name?: string;
  itemListElement?: ListItem[];
}

export interface WebPageElement extends Base {
  '@type': RichResultsTypes.WebPageElement;
  description?: string;
  text?: string;
}

export interface WebPage extends Base {
  '@type': RichResultsTypes.WebPage;
  headline?: string;
  mainEntity?: Thing | Thing[];
}

export function create<T extends Base>(
  type: RichResultsTypes,
  data: Omit<T, '@type'>
): T & { '@context': string } {
  return {
    '@type': type,
    ...(data as any),
  };
}
